import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toggleSidebar } from '../redux/actions/sidebar/sidebarAction';
import { logout } from '../redux/actions/login/loginAction';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import {
  AppBar, Toolbar, IconButton, Typography, Drawer, List, ListItem,
  ListItemText, Box, CssBaseline, Button, Tabs, Tab, Avatar,
  ListItemIcon, Collapse, Divider
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import BusinessIcon from '@mui/icons-material/Business';
import InventoryIcon from '@mui/icons-material/Inventory';
import DiamondIcon from '@mui/icons-material/Diamond';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import CategoryIcon from '@mui/icons-material/Category';
import LogoutIcon from '@mui/icons-material/Logout';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import HistoryIcon from '@mui/icons-material/History';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import AssessmentIcon from '@mui/icons-material/Assessment';
import Swal from 'sweetalert2';
import { fetchImportedInventory } from '../redux/actions/importedInventory/importedInventoryAction';
import { toast } from 'react-toastify';

const drawerWidth = 280;

const Dashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isSidebarOpen = useSelector((state) => state.sidebar.isSidebarOpen);
  const user = useSelector((state) => state.login.user);

  // Debug: Log user data
  console.log('User data from Redux:', user);
  console.log('Username:', user?.username);

  const [transactionSubmenuOpen, setTransactionSubmenuOpen] = useState(false);
  const [reportsSubmenuOpen, setReportsSubmenuOpen] = useState(false);

  // Debug: Log sidebar state
  console.log('Sidebar state:', isSidebarOpen);

  // Ensure sidebar is open by default
  // useEffect(() => {
  //   if (!isSidebarOpen) {
  //     dispatch(toggleSidebar());
  //   }
  // }, [dispatch, isSidebarOpen]);

  const handleLogout = async () => {
    const result = await Swal.fire({
      title: 'Logout Confirmation',
      text: 'Are you sure you want to logout?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, Logout',
      cancelButtonText: 'Stay Logged In',
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      reverseButtons: true
    });

    if (result.isConfirmed) {
      dispatch(logout());
      localStorage.removeItem('token');
      navigate('/login');
    }
  };

  const handleToggle = () => {
    dispatch(toggleSidebar());
  };

  const menuItems = [
    { 
      text: 'Dashboard', 
      icon: <DashboardIcon />,
      onClick: () => {
        navigate('/dashboard');
      }
    },
    { 
      text: 'Masters', 
      icon: <PeopleIcon />,
      onClick: () => {
        navigate('/masters/user');
      }
    },
    {
      text: 'Import Batch',
      icon: <UploadFileIcon />,
      onClick: async () => {
        navigate('/import-batch');
        // Fetch active imported inventory data when menu is clicked
        try {
          await dispatch(fetchImportedInventory());
          toast.success('Active imported inventory data fetched successfully!');
        } catch (error) {
          console.error('Error fetching active imported inventory:', error);
        }
      }
    },
    {
      text: 'CSV Import Logs',
      icon: <HistoryIcon />,
      onClick: () => {
        navigate('/csv-import-logs');
      }
    },
    { 
      text: 'Transactions', 
      icon: <InventoryIcon />,
      onClick: () => {
        setTransactionSubmenuOpen(!transactionSubmenuOpen);
      }
    },
    { 
      text: 'Reports', 
      icon: <CategoryIcon />,
      onClick: () => {
        setReportsSubmenuOpen(!reportsSubmenuOpen);
      }
    },
  ];

  const masterTabs = [
    { 
      label: 'User', 
      icon: <PeopleIcon />,
      path: '/masters/user'
    },
    { 
      label: 'Departments', 
      icon: <BusinessIcon />,
      path: '/masters/department'
    },
    { 
      label: 'Employee', 
      icon: <PeopleIcon />,
      path: '/masters/employee'
    },
    { 
      label: 'Stock Type', 
      icon: <InventoryIcon />,
      path: '/masters/stock'
    },
    { 
      label: 'Purity', 
      icon: <DiamondIcon />,
      path: '/masters/purity'
    },
    { 
      label: 'Color', 
      icon: <ColorLensIcon />,
      path: '/masters/color'
    },
    { 
      label: 'Stone Type', 
      icon: <DiamondIcon />,
      path: '/masters/stone'
    },
    { 
      label: 'Stock Code', 
      icon: <InventoryIcon />,
      path: '/masters/raw-stock-code'
    },
  ];

  const transactionItems = [
    { text: 'Raw Material Stock', icon: <InventoryIcon />, path: '/transaction/raw-stock' },
    { text: 'Raw Stock History', icon: <HistoryIcon />, path: '/transaction/raw-stock-history' },
    { text: 'Stock Issue', icon: <RemoveIcon />, path: '/transaction/stock-issue' },
    { text: 'Stock Receive', icon: <AddIcon />, path: '/transaction/stock-receive' },
    { text: 'Stock Return', icon: <RemoveIcon />, path: '/transaction/stock-return' },
    // { text: 'Broken Stone Entry', icon: <BrokenImageIcon />, path: '/transaction/broken-stone-entry' },
    { text: 'Inventory Process', icon: <InventoryIcon />, path: '/transaction/inventory-process' },
  ];

  const reportItems = [
    {
      text: 'Department Wise Pending',
      icon: <AssessmentIcon />,
      path: '/reports/department-wise-pending'
    },
    {
      text: 'Department Wise Production',
      icon: <AssessmentIcon />,
      path: '/reports/department-wise-production'
    },
    {
      text: 'Raw Material Stock',
      icon: <AssessmentIcon />,
      path: '/reports/raw-material-stock'
    },
    {
      text: 'Raw Gold Stock Issued',
      icon: <AssessmentIcon />,
      path: '/reports/raw-gold-issued'
    },
    {
      text: 'Stone Issued',
      icon: <AssessmentIcon />,
      path: '/reports/stone-issued'
    },
    {
      text: 'Gold Stock Added',
      icon: <AssessmentIcon />,
      path: '/reports/gold-stock-added'
    },
    {
      text: 'Stone Stock Added',
      icon: <AssessmentIcon />,
      path: '/reports/stone-stock-added'
    },
    {
      text: 'Stock Received',
      icon: <AssessmentIcon />,
      path: '/reports/stock-received'
    },
  ];

  // Get the current active tab based on the URL
  const getActiveTab = () => {
    const path = location.pathname;
    const tab = masterTabs.find(tab => path.includes(tab.path));
    return tab ? tab.label : 'User';
  };

  const handleTabChange = (event, newValue) => {
    const selectedTab = masterTabs.find(tab => tab.label === newValue);
    if (selectedTab) {
      navigate(selectedTab.path);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      <CssBaseline />
      <AppBar position="fixed" sx={{ zIndex: 1300, background: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
        <Toolbar>
          <IconButton 
            color="inherit" 
            edge="start" 
            onClick={handleToggle}
            sx={{
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600 }}>
            Order Inventory System
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'primary.light' }}>
              {user?.username?.charAt(0)?.toUpperCase() || 'A'}
            </Avatar>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {user?.username ? user.username.charAt(0).toUpperCase() + user.username.slice(1) : 'Administrator'}
            </Typography>
            <Button 
              onClick={handleLogout} 
              variant="outlined" 
              startIcon={<LogoutIcon />}
              sx={{ 
                color: 'white', 
                borderColor: 'rgba(255, 255, 255, 0.5)',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              Logout
            </Button>
          </Box>
        </Toolbar>
      </AppBar>
      <Drawer
        variant="persistent"
        open={isSidebarOpen}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            background: 'white',
            color: 'inherit',
            borderRight: '1px solid rgba(0, 0, 0, 0.12)',
          },
        }}
      >
        <Toolbar />
        <Box sx={{ width: drawerWidth, overflowY: 'auto', '&::-webkit-scrollbar': {display: 'none'}, msOverflowStyle: 'none', scrollbarWidth: 'none' }}>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'left' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'inherit' }}>Navigation</Typography>
          </Box>
          <List sx={{ pt: 0 }}>
            {menuItems.map((item) => (
              <React.Fragment key={item.text}>
                <ListItem
                  button
                  onClick={item.onClick}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.08)',
                    },
                    borderRadius: '4px',
                    margin: '4px 8px',
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
                  {item.text === 'Transactions' && (transactionSubmenuOpen ? <ExpandLess /> : <ExpandMore />)}
                  {item.text === 'Reports' && (reportsSubmenuOpen ? <ExpandLess /> : <ExpandMore />)}
                </ListItem>
                {item.text === 'Transactions' && (
                  <Collapse in={transactionSubmenuOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {transactionItems.map((subItem, index) => (
                        <ListItem
                          button
                          key={index}
                          sx={{
                            pl: 4,
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.08)',
                            },
                            borderRadius: '4px',
                            margin: '2px 8px',
                          }}
                          onClick={() => navigate(subItem.path)}
                        >
                          <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>{subItem.icon}</ListItemIcon>
                          <ListItemText primary={subItem.text} />
              </ListItem>
            ))}
          </List>
                  </Collapse>
                )}
                {item.text === 'Reports' && (
                  <Collapse in={reportsSubmenuOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                      {reportItems.map((subItem, index) => (
                        <ListItem
                          button
                          key={index}
                          sx={{
                            pl: 4,
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.08)',
                            },
                            borderRadius: '4px',
                            margin: '2px 8px',
                          }}
                          onClick={() => navigate(subItem.path)}
                        >
                          <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>{subItem.icon}</ListItemIcon>
                          <ListItemText primary={subItem.text} />
                  </ListItem>
                ))}
              </List>
            </Collapse>
                )}
                {['Masters', 'CSV Import Logs'].includes(item.text) && <Divider sx={{ my: 1 }} />}
              </React.Fragment>
            ))}
          </List>
        </Box>
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${isSidebarOpen ? drawerWidth : 0}px)` },
          ml: { sm: `${isSidebarOpen ? drawerWidth : 0}px` },
          transition: 'margin 0.2s ease-in-out, width 0.2s ease-in-out',
          mt: '64px', // Height of AppBar
          minHeight: 'calc(100vh - 64px)', // Ensure full height
        }}
      >
        {location.pathname.startsWith('/masters') && (
          <Box sx={{ mb: 3 }}>
            <Tabs 
              value={getActiveTab()} 
              onChange={handleTabChange} 
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                  minHeight: 48,
                },
                '& .Mui-selected': {
                  color: 'primary.main',
                  fontWeight: 600,
                },
              }}
            >
              {masterTabs.map((tab) => (
                <Tab 
                  key={tab.label} 
                  label={tab.label} 
                  value={tab.label}
                  icon={tab.icon}
                  iconPosition="start"
                />
              ))}
            </Tabs>
          </Box>
        )}
        <Outlet />
      </Box>

      <Box
        component="footer"
        sx={{
          py: 2,
          px: 2,
          mt: 'auto',
          background: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)',
          color: 'white',
          textAlign: 'center',
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          Developed By appsinlive © 2025
        </Typography>
      </Box>
    </Box>
  );
};

export default Dashboard;
